"use client"

import { Button } from "@/components/ui/button"
import { ArrowRight, Facebook, Linkedin, Twitter } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useLanguage } from "@/store/language-store"
import { cn } from "@/lib/utils"
import { useTheme } from "../ThemeProvider"
import { useNavigate } from "react-router-dom"
import LogoDark from '../../assets/logo-dark.svg?url'
import LogoLight from '../../assets/logo-light.svg?url'

export function Footerdemo() {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const { theme } = useTheme()
  const navigate = useNavigate()
  const isRTL = language === 'ar'
  const isDark = theme === 'dark'

  const columns = [
    {
      title: 'footer.product.title',
      links: [
        { label: 'footer.product.features', href: '#features' },
        { label: 'footer.product.pricing', href: '#pricing' },
        { label: 'footer.product.faq', href: '#faq' },
        { label: 'footer.product.support', href: '/support' },
      ]
    },
    {
      title: 'footer.company.title',
      links: [
        { label: 'footer.company.about', href: '/about' },
        { label: 'footer.company.blog', href: '/blog' },
        { label: 'footer.company.careers', href: '/careers' },
        { label: 'footer.company.contact', href: '/contact' },
      ]
    },
    {
      title: 'footer.legal.title',
      links: [
        { label: 'footer.legal.privacy', href: '/privacy' },
        { label: 'footer.legal.terms', href: '/terms' },
        { label: 'footer.legal.cookies', href: '/cookies' },
        { label: 'footer.legal.licenses', href: '/licenses' },
      ]
    },
  ]

  const handleNavigation = (href: string) => {
    if (href.startsWith('#')) {
      const element = document.getElementById(href.replace('#', ''))
      if (element) {
        const navHeight = 64 // height of navbar
        const y = element.getBoundingClientRect().top + window.pageYOffset - navHeight
        window.scrollTo({ top: y, behavior: 'smooth' })
      }
    } else {
      navigate(href)
    }
  }

  return (
    <footer className="bg-background-secondary dark:bg-background-dark-secondary border-t border-border dark:border-border-dark">
      <div className="container mx-auto py-12 px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Logo and Description */}
          <div className="space-y-4">
            <a href="/" className="block">
              <img
                src={isDark ? LogoDark : LogoLight}
                alt="Flows.Cash Logo"
                className="h-8 w-auto mb-4"
              />
            </a>
            <p className="text-text-secondary dark:text-text-dark-secondary">
              {t('footer.description')}
            </p>
            <div className="flex space-x-4">
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="text-text-secondary hover:text-secondary dark:text-text-dark-secondary dark:hover:text-primary-dark transition-colors duration-200">
                <Twitter className="h-5 w-5" />
              </a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-text-secondary hover:text-secondary dark:text-text-dark-secondary dark:hover:text-primary-dark transition-colors duration-200">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-text-secondary hover:text-secondary dark:text-text-dark-secondary dark:hover:text-primary-dark transition-colors duration-200">
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Links Columns */}
          {columns.map((column, index) => (
            <div key={index} className="space-y-4">
              <h3 className="text-text-primary dark:text-text-dark-primary font-semibold">
                {t(column.title)}
              </h3>
              <ul className="space-y-2">
                {column.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <button
                      onClick={() => handleNavigation(link.href)}
                      className="text-text-secondary hover:text-secondary dark:text-text-dark-secondary dark:hover:text-primary-dark transition-colors duration-200"
                    >
                      {t(link.label)}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="mt-12 pt-8 border-t border-border dark:border-border-dark">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
                {t('footer.newsletter.title')}
              </h3>
              <p className="text-text-secondary dark:text-text-dark-secondary">
                {t('footer.newsletter.description')}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                placeholder={t('footer.newsletter.placeholder')}
                className="flex-1 px-4 py-2 rounded-lg bg-background-input dark:bg-background-dark-input border border-border dark:border-border-dark focus:outline-none focus:ring-2 focus:ring-secondary dark:focus:ring-primary-dark text-text-primary dark:text-text-dark-primary"
              />
              <Button 
                className="bg-primary text-white hover:bg-primary/90 dark:bg-primary-dark dark:hover:bg-primary-dark/90"
                onClick={() => {}}
              >
                {t('footer.newsletter.button')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 pt-8 border-t border-border dark:border-border-dark text-center text-text-secondary dark:text-text-dark-secondary">
          <p>
            &copy; {new Date().getFullYear()} Flows.Cash. {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  )
} 